import { Grid } from "@material-ui/core";
import Container from "@saleor/components/Container";
import PageHeader from "@saleor/components/PageHeader";
import { defaultListSettings } from "@saleor/config";
import { contractDetailUrl } from "@saleor/createOrder/urls";
import {
  exporPiListUrl,
  ExporPiListUrlQueryParams
} from "@saleor/createOrderExport/urls";
import { useExportPisQuery } from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useListSettings from "@saleor/hooks/useListSettings";
import useLocalPaginator, {
  useLocalPaginationState
} from "@saleor/hooks/useLocalPaginator";
import useNavigator from "@saleor/hooks/useNavigator";
import { makeStyles } from "@saleor/macaw-ui";
import { ListViews } from "@saleor/types";
import { mapEdgesToItems } from "@saleor/utils/maps";
import { getSortParams, getSortUrlVariables } from "@saleor/utils/sort";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import FullScreenButton from "../../components/FullScreenButton";
import SearchBar from "../../components/Searchbar";
import SoldToList from "../../components/SoldToList";
import { getSortQueryPiVariables } from "./sort";

interface SoldToListProps {
  params: ExporPiListUrlQueryParams;
}

interface StyleProps {
  fullScreen?: boolean;
}

const useStyles = makeStyles<StyleProps>(
  _ => ({
    pageHeader: {
      marginBottom: "1.2rem",
      "& > :first-child": {
        visibility: props => (props.fullScreen ? "hidden" : "visible"),
        margin: 0,
        marginBottom: props => (props.fullScreen ? "0.4rem" : "1.2rem")
      },
      "& > :nth-child(2)": {
        alignSelf: "flex-end"
      }
    },
    searchBar: {
      display: props => (props.fullScreen ? "none" : "block")
    }
  }),
  { name: "SoldToView" }
);

export enum SearchTypeEnum {
  SOLDTO = "SoldTo",
  PI = "Pi"
}

const SoldToView: React.FC<SoldToListProps> = ({ params }) => {
  const intl = useIntl();
  const [
    { fullScreen, searchIdExport, tabSoldToExport },
    dispatchAppState
  ] = useAppState();
  const classes = useStyles({ fullScreen });
  const navigate = useNavigator();
  const [searchId, setSearchId] = useState<string | null>(null);
  const [type, setType] = useState<SearchTypeEnum>(SearchTypeEnum.SOLDTO);

  const { updateListSettings, settings } = useListSettings(
    ListViews.SOLD_TO_LIST
  );

  useEffect(() => {
    updateListSettings("rowNumber", defaultListSettings.SOLD_TO_LIST.rowNumber);
    if (searchIdExport) {
      setSearchId(searchIdExport);
      if (tabSoldToExport === 1) {
        setType(SearchTypeEnum.SOLDTO);
      } else {
        setType(SearchTypeEnum.PI);
      }
    }
    return () => {
      dispatchAppState({
        payload: {
          value: false
        },
        type: "setFullScreen"
      });
    };
  }, []);

  const [
    valuesPaginationState,
    setValuesPaginationState
  ] = useLocalPaginationState(settings?.rowNumber);

  const resetPaginationState = () => {
    setValuesPaginationState({
      first: settings?.rowNumber,
      after: undefined,
      before: undefined
    });
  };

  const formatValueSearch = value => {
    if (value) {
      const arrValue = value.split("-");
      return arrValue.length ? arrValue[0].trim() : "";
    }
    return "";
  };

  const paginateValues = useLocalPaginator(setValuesPaginationState);
  const queryVariables = React.useMemo(
    () => ({
      ...valuesPaginationState,
      filter: {
        soldTo:
          (type === SearchTypeEnum.SOLDTO &&
            searchId &&
            formatValueSearch(searchId)) ||
          undefined,
        code: (type === SearchTypeEnum.PI && searchId) || undefined
      },
      sortBy: getSortQueryPiVariables(params)
    }),
    [searchId, settings.rowNumber, valuesPaginationState, params, type]
  );

  const { data: dataSearch, loading: loadingSearchBar } = useExportPisQuery({
    skip: !searchId,
    displayLoader: true,
    variables: queryVariables
  });

  const lastPaginationState = () => {
    setValuesPaginationState({
      last: dataSearch?.exportPis?.latestPageItemNumber
    });
  };
  const { loadNextPage, loadPreviousPage, pageInfo } = paginateValues(
    dataSearch?.exportPis?.pageInfo,
    valuesPaginationState
  );
  const handleOnChangeSearch = (value, type) => {
    setSearchId(value);
    setType(type);
  };
  const dataSearchBar = useMemo(() => mapEdgesToItems(dataSearch?.exportPis), [
    dataSearch?.exportPis
  ]);

  useEffect(() => {
    if (dataSearchBar && !dataSearchBar.length) {
      dispatchAppState({
        payload: {
          searchIdExport: ""
        },
        type: "setSearchIdExport"
      });
    }
  }, [dataSearchBar]);

  const handlePreviousFirstPage = () => {
    resetPaginationState();
  };
  const handleNextLastPage = () => {
    lastPaginationState();
  };
  const handleSort = field => {
    resetPaginationState();
    navigate(
      exporPiListUrl({
        ...params,
        ...getSortUrlVariables(field, params)
      }),
      { replace: true }
    );
  };
  useEffect(() => {
    resetPaginationState();
  }, [settings?.rowNumber, searchId]);

  const viewContractDetail = contractId => {
    navigate(contractDetailUrl(contractId));
    dispatchAppState({
      payload: {
        searchIdExport: searchId
      },
      type: "setSearchIdExport"
    });
  };

  return (
    <Container>
      <PageHeader
        className={classes.pageHeader}
        title={intl.formatMessage({
          defaultMessage: "Search",
          description: "label"
        })}
      >
        {loadingSearchBar || dataSearchBar?.length ? (
          <FullScreenButton />
        ) : null}
      </PageHeader>
      <Grid container spacing={3}>
        <Grid item xs={fullScreen ? false : 3} className={classes.searchBar}>
          <SearchBar
            onSearch={handleOnChangeSearch}
            searchId={searchId || searchIdExport}
            loading={loadingSearchBar}
            dataFound={!!dataSearchBar?.length}
            soldToExists={!!dataSearchBar?.length && searchId !== ""}
          />
        </Grid>
        <Grid item xs={fullScreen ? 12 : 9}>
          {loadingSearchBar || dataSearchBar?.length > 0 ? (
            <SoldToList
              settings={settings}
              disabled={loadingSearchBar}
              data={dataSearchBar}
              onNextPage={loadNextPage}
              onPreviousPage={loadPreviousPage}
              onUpdateListSettings={updateListSettings}
              onNextLastPage={handleNextLastPage}
              onPreviousFirstPage={handlePreviousFirstPage}
              onRowClick={() => null}
              totalCount={dataSearch?.exportPis?.totalCount}
              latestPageItemNumber={dataSearch?.exportPis?.latestPageItemNumber}
              pageInfo={pageInfo}
              loading={loadingSearchBar}
              onSort={handleSort}
              sort={getSortParams(params)}
              onViewContract={viewContractDetail}
            />
          ) : null}
        </Grid>
      </Grid>
    </Container>
  );
};

export default SoldToView;
